import React, { useState, useEffect } from 'react';
import { Bell, Search, User, Menu, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import NotificationCenter from '../NotificationCenter';
import { useAuth } from '../../contexts/AuthContext';

const Header = ({ user, onSearch, onMenuClick, userRole }) => {
  const { logout } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Get current user from localStorage or fallback to prop
  const getCurrentUser = () => {
    try {
      const savedUser = localStorage.getItem('landlord_user');
      if (savedUser) {
        return JSON.parse(savedUser);
      }
    } catch (error) {
      console.error('Error parsing saved user:', error);
    }
    return user || { name: 'User', role: userRole || 'landlord' };
  };

  const currentUser = getCurrentUser();

  useEffect(() => {
    // Only fetch unread count for landlords (notifications are for property owners)
    if (userRole === 'landlord') {
      fetchUnreadCount();
      
      // Set up polling for real-time updates
      const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [userRole]);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread`);
      const data = await response.json();
      setUnreadCount(data.count || 0);
    } catch (error) {
      console.error('Failed to fetch unread notifications count:', error);
    }
  };

  const handleNotificationClick = () => {
    console.log('Notification bell clicked, opening notification center');
    setShowNotifications(true);
  };

  const handleNotificationClose = () => {
    setShowNotifications(false);
    // Refresh unread count when closing
    if (userRole === 'landlord') {
      fetchUnreadCount();
    }
  };

  const handleLogout = async () => {
    try {
      console.log('🔄 Logging out...');
      
      // Use AuthContext logout method
      await logout();
      
      console.log('✅ Logout successful');
      
      // Navigate to landing page
      window.location.href = '/';
      
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Clear auth and redirect anyway
      localStorage.removeItem('landlord_token');
      localStorage.removeItem('landlord_user');
      window.location.href = '/';
    }
  };
  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6">
      {/* Mobile menu button and branding */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Mobile logo/title - only show on small screens when sidebar is closed */}
        <div className="lg:hidden">
          <h1 className="text-lg font-bold text-gray-900">Legacy Property</h1>
        </div>
      </div>

      {/* Search - hidden on mobile, shown on larger screens */}
      <div className="hidden md:flex flex-1 max-w-md mx-6">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={userRole === 'tenant' ? 'Search documents, payments...' : 'Search properties, tenants...'}
            className="pl-10 pr-4 h-9 w-full"
            onChange={(e) => onSearch?.(e.target.value)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Search button for mobile */}
        <Button variant="ghost" size="icon" className="md:hidden">
          <Search className="h-5 w-5" />
        </Button>

        {/* Notifications - only show for landlords */}
        {userRole === 'landlord' && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative" 
            onClick={handleNotificationClick}
          >
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        )}

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage src="" alt={currentUser.name} />
                <AvatarFallback>
                  {currentUser.name?.split(' ').map(n => n[0]).join('') || 'U'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <div className="flex items-center justify-start gap-2 p-2">
              <div className="flex flex-col space-y-1 leading-none">
                <p className="font-medium">{currentUser.name}</p>
                <p className="w-[200px] truncate text-sm text-muted-foreground">
                  {currentUser.email || `${currentUser.role}`}
                </p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Notification Center */}
      {userRole === 'landlord' && (
        <NotificationCenter 
          isOpen={showNotifications} 
          onClose={handleNotificationClose} 
        />
      )}
    </header>
  );
};

export default Header;